/**
 * Die Pakete — und die einzige Stelle im Quelltext, die Paketdaten holt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM HIER KEINE ZAHL STEHT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Bis zum Vorgang 010 stand der Abopreis an vier Stellen gleichzeitig: in
 * `plans`, in einer Tabelle `PRICES` in `BillingSetupView`, in einer
 * verdrahteten Zeile des Testphasen-Bands und — daraus gerechnet — in
 * `trial_conversions_due`, also genau in dem Feld, aus dem das AUVISO CRM die
 * Rechnung erzeugt. Keine dieser vier Stellen war böswillig; jede war der
 * kürzeste Weg an ihrem Tag. Vier Orte für eine Zahl laufen trotzdem
 * auseinander, und zwar still: der Bildschirm zeigt dann das eine, die Rechnung
 * sagt das andere.
 *
 * Seit Migration 0060 lebt der Preis an EINER Stelle, nämlich in `plans`.
 * Dieses Modul LIEST ihn und rechnet ihn nicht. Insbesondere:
 *
 *  - Der Aufschlag von 30 % für das Monatsabo existiert genau einmal, in der
 *    Erzeugungsvorschrift der Spalte `price_month`. Eine zweite Multiplikation
 *    hier wäre eine zweite Rundungsart — und ein halber Cent Unterschied
 *    zwischen Bildschirm und Rechnung ist genau die Art von Fehler, die
 *    niemandem auffällt, bis ein Vermieter nachrechnet.
 *  - Es gibt KEINEN verdrahteten Ersatzwert. Lassen sich die Pakete nicht laden,
 *    zeigt die Oberfläche gar keinen Preis. Ein Rückfallwert wäre der fünfte
 *    Preisort und der gefährlichste, weil er nur dann erscheint, wenn niemand
 *    hinsieht.
 *
 * Weil die Pakete Daten sind, braucht eine Verschiebung von Band oder Betrag
 * keinen Build, keinen Netlify-Deploy und keine App-Einreichung bei Apple —
 * nur ein `update`. In der Hülle wäre eine verdrahtete Zahl mehrere Tage
 * Prüfzeit von der Wirklichkeit entfernt.
 *
 * `src/data/pakete.test.ts` hält das durch: es durchsucht die Quelldateien und
 * schlägt fehl, sobald irgendwo wieder ein Betrag im Code steht.
 */

import { useEffect, useState } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useStore } from './store'
import type { ContractTerm } from './stripe'

export interface Paket {
  code: string
  /** `{ de: …, en: … }` wie überall in diesem Schema. */
  name: Record<string, string>
  /** Untere Bandkante, einschließlich. */
  min_units: number
  /** Obere Bandkante, einschließlich. `null` heißt: ohne Obergrenze. */
  max_units: number | null
  /** Preis je Monat im Jahresvertrag. `null` heißt: auf Anfrage. */
  price_base_month: number | null
  /** Monatsabo je Monat. Von der Datenbank erzeugt, hier nur gelesen. */
  price_month: number | null
  /** Jahresvertrag je Jahr. Von der Datenbank erzeugt, hier nur gelesen. */
  price_year: number | null
  currency: string
}

const SPALTEN =
  'code,name,min_units,max_units,price_base_month,price_month,price_year,currency,sort_order'

function zahl(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function normalise(row: Record<string, unknown>): Paket {
  return {
    code: String(row.code),
    name: (row.name ?? {}) as Record<string, string>,
    min_units: Number(row.min_units),
    max_units: zahl(row.max_units),
    price_base_month: zahl(row.price_base_month),
    price_month: zahl(row.price_month),
    price_year: zahl(row.price_year),
    currency: String(row.currency),
  }
}

/**
 * Die aktiven Pakete, aufsteigend nach Bandkante.
 *
 * Ohne Anmeldung lesbar: die Policy `plans_public` steht seit 0003 dafür da,
 * damit eine öffentliche Preisseite rechnen kann. Stillgelegte Alttarife
 * (`starter`, `standard`) filtert dieselbe Policy heraus.
 */
export async function ladePakete(sb: SupabaseClient): Promise<Paket[]> {
  const { data, error } = await sb
    .from('plans').select(SPALTEN).eq('active', true).order('min_units')
  if (error) throw new Error(`Pakete laden: ${error.message}`)
  return (data ?? []).map((r) => normalise(r as Record<string, unknown>))
}

/**
 * Welches Paket deckt diese Einheitenzahl? Reine Auswahl, keine Rechnung.
 *
 * `null` heißt: keines deckt sie. Das passiert bei 0, bei negativen Zahlen und
 * bei einer LÜCKE zwischen zwei Bändern. Es wird dann ausdrücklich NICHT auf
 * ein Nachbarpaket ausgewichen — weder auf das kleinste noch auf das größte.
 * Ein stiller Rückfall wäre ein Betrag, den nie jemand vereinbart hat, und er
 * fiele erst auf, wenn der Kunde die Rechnung liest.
 */
export function paketFuer(pakete: Paket[], einheiten: number): Paket | null {
  return pakete.find(
    (p) => einheiten >= p.min_units && (p.max_units === null || einheiten <= p.max_units),
  ) ?? null
}

/**
 * Der anzuzeigende Betrag JE MONAT für diese Vertragsform.
 *
 * `null` heißt „auf Anfrage" und niemals `0` und niemals ein Ersatzwert. Im
 * Jahresvertrag ist das `price_base_month` — der gepflegte Betrag selbst, nicht
 * `price_year / 12`, denn geteilt würde hier ein zweites Mal gerundet.
 */
export function preisJeMonat(paket: Paket, term: ContractTerm): number | null {
  return term === 'yearly' ? paket.price_base_month : paket.price_month
}

/**
 * Der Betrag, der tatsächlich abgebucht wird, samt Zeitraum.
 *
 * Im Jahresvertrag stehen auf dem Bildschirm zwangsläufig zwei Zahlen: der
 * Betrag je Monat und der Betrag je Jahr. Das ist kein Widerspruch, sondern die
 * Vertragsform — und wer nur den Monatswert zeigt, lässt einen Vermieter den
 * kleineren Betrag lesen und den größeren bezahlen.
 */
export function preisJePeriode(
  paket: Paket, term: ContractTerm,
): { betrag: number | null; periode: 'month' | 'year' } {
  return term === 'yearly'
    ? { betrag: paket.price_year, periode: 'year' }
    : { betrag: paket.price_month, periode: 'month' }
}

export interface PaketStand {
  /** Das gebuchte Paket. `null` in der Demo und solange nichts geladen ist. */
  paket: Paket | null
  einheiten: number
  /** Obergrenze des gebuchten Pakets. `null` heißt: ohne Obergrenze. */
  grenze: number | null
  /** Wie viele Einheiten noch frei sind. Negativ heißt: über dem Band. */
  frei: number | null
  /** Welches Paket die NÄCHSTE Einheit verlangt. */
  naechstes: Paket | null
}

/**
 * Wo steht dieser Betrieb? Reine Funktion, ohne React — damit die Aussage
 * prüfbar ist, an der die Oberfläche das Höherstufen aufhängt. Es gibt in
 * diesem Projekt keine Testumgebung für Oberflächen; was hier nicht heraussteht,
 * ist dauerhaft Handarbeit.
 *
 * `unitsLimit` schlägt die Bandkante des Pakets, und zwar in dieser Reihenfolge:
 * die Spalte bedeutet seit 0060 „von Hand gesetzte Ausnahme", `null` heißt „es
 * gilt das Paket". Das ist der Notausgang, wenn ein einzelner Betrieb klemmt.
 */
export function paketStand(
  pakete: Paket[],
  planCode: string | null,
  einheiten: number,
  unitsLimit: number | null,
): PaketStand {
  const paket = planCode ? pakete.find((p) => p.code === planCode) ?? null : null
  const grenze = unitsLimit ?? paket?.max_units ?? null
  return {
    paket,
    einheiten,
    grenze,
    frei: grenze === null ? null : grenze - einheiten,
    naechstes: paketFuer(pakete, einheiten + 1),
  }
}

/**
 * Die geladenen Pakete, einmal je Sitzung geholt.
 *
 * Der Zwischenspeicher liegt im Modul und nicht in einem Zustandshalter: die
 * Liste hat vier Zeilen, mehrere Ansichten fragen sie gleichzeitig, und zwei
 * Abfragen für dieselbe Tabelle wären nur Rundreisen. Ein Neuladen der Seite
 * holt sie frisch — genau das ist die Bedingung, unter der eine Preisänderung
 * ohne Auslieferung wirkt.
 */
let geholt: Promise<Paket[]> | null = null

export function paketeNeuLaden(): void {
  geholt = null
}

export function usePakete(): { pakete: Paket[]; geladen: boolean } {
  const [pakete, setPakete] = useState<Paket[]>([])
  const [geladen, setGeladen] = useState(false)

  useEffect(() => {
    let lebt = true
    if (!supabase) { setGeladen(true); return }
    if (!geholt) geholt = ladePakete(supabase)
    geholt
      .then((liste) => { if (lebt) setPakete(liste) })
      .catch(() => {
        // Kein Ersatzwert. Die Aufrufer zeigen dann keinen Preis, und das ist
        // die richtige Antwort — eine erfundene Zahl wäre die falsche.
        geholt = null
      })
      .finally(() => { if (lebt) setGeladen(true) })
    return () => { lebt = false }
  }, [])

  return { pakete, geladen }
}

/**
 * Der Stand des angemeldeten Betriebs.
 *
 * In der Demo gibt es keine Abonnementzeile, also auch kein Paket und keine
 * Grenze — dieselbe Haltung wie in `tenant_writable()`: kein Abonnement heißt
 * „nicht blockieren", nicht „alles sperren". Die öffentliche Preisliste zeigt
 * die Demo trotzdem; sie ist das Verkaufsargument.
 */
export function usePaketStand(): PaketStand & { pakete: Paket[]; geladen: boolean } {
  const store = useStore()
  const { pakete, geladen } = usePakete()
  const abo = store.subscription

  const stand = paketStand(
    pakete,
    abo ? geltenderPlanCode(abo) : null,
    store.db.units.length,
    abo?.units_limit ?? null,
  )
  return { ...stand, pakete, geladen }
}

/**
 * Eine fällige Herabstufung gilt, sobald ihr Zeitpunkt erreicht ist — dieselbe
 * Auswertung wie `aktives_paket()` in der Datenbank. Sie wird beim Lesen
 * gemacht und nicht von einem Cron materialisiert: kein Job, der ausfallen
 * kann, und keine zwei Zeilen, die auseinanderlaufen.
 */
export function geltenderPlanCode(abo: {
  plan_code: string
  plan_pending_code: string | null
  plan_pending_at: string | null
}): string {
  if (abo.plan_pending_code && abo.plan_pending_at
      && new Date(abo.plan_pending_at).getTime() <= Date.now()) {
    return abo.plan_pending_code
  }
  return abo.plan_code
}
